import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../api';
import AuthHeader from '../components/Header/AuthHeader';
import Button from '../components/Button';
import '@fortawesome/fontawesome-free/css/all.min.css';

const SifreOlustur: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('from') || '/profil';
  const { user, refreshUser } = useAuth();

  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    if (user === undefined) return;
    if (user === null) {
      navigate('/giris', { state: { from: '/sifre-olustur' } });
      return;
    }
    if (user.hasPassword !== false) {
      navigate(returnTo.startsWith('/') ? returnTo : '/profil', { replace: true });
    }
  }, [user, navigate, returnTo]);

  const handleRequestCode = async (): Promise<void> => {
    setFormError('');
    setInfoMessage('');
    setSendingCode(true);
    try {
      const result = await authService.requestCreatePasswordCode();
      if (result.isSuccess) {
        setCodeSent(true);
        setInfoMessage(result.message ?? 'Doğrulama kodu e-posta adresinize gönderildi.');
      } else {
        setFormError(result.message ?? 'Kod gönderilemedi.');
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Kod gönderilemedi.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError('');

    const code = verificationCode.trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setFormError('Geçerli bir 6 haneli doğrulama kodu girin.');
      return;
    }
    if (!newPassword.trim()) {
      setFormError('Yeni şifre girin.');
      return;
    }
    if (newPassword !== newPasswordConfirmation) {
      setFormError('Yeni şifre ve tekrar alanları eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      const result = await authService.createPassword({
        verificationCode: code,
        newPassword,
        newPasswordConfirmation,
      });
      if (result.isSuccess) {
        await refreshUser();
        alert(result.message ?? 'Şifreniz oluşturuldu.');
        navigate(returnTo.startsWith('/') ? returnTo : '/profil', { replace: true });
      } else {
        setFormError(result.message ?? 'Şifre oluşturulamadı.');
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Şifre oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  };

  if (user === null || user === undefined || user.hasPassword !== false) {
    return null;
  }

  return (
    <>
      <AuthHeader />
      <div className="sb-page-bg min-vh-100 d-flex align-items-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h4 className="card-title text-center mb-3">Şifre Oluştur</h4>
                  <p className="text-muted text-center mb-4">
                    Google ile giriş yaptığınız için hesabınızda henüz şifre yok. Güvenlik işlemleri için
                    e-posta doğrulama kodu ile şifre belirleyin.
                  </p>

                  {formError && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {formError}
                    </div>
                  )}
                  {infoMessage && (
                    <div className="alert alert-info mb-3" role="alert">
                      {infoMessage}
                    </div>
                  )}

                  {!codeSent ? (
                    <Button
                      type="button"
                      className="btn btn-primary w-100 mb-3"
                      loading={sendingCode}
                      onClick={handleRequestCode}
                    >
                      E-postama Doğrulama Kodu Gönder
                    </Button>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="verificationCode">
                          E-posta Doğrulama Kodu
                        </label>
                        <input
                          className="form-control form-control-lg text-center"
                          id="verificationCode"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="000000"
                          value={verificationCode}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '');
                            if (v.length <= 6) setVerificationCode(v);
                          }}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="newPassword">
                          Yeni Şifre
                        </label>
                        <input
                          className="form-control"
                          id="newPassword"
                          type="password"
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-4">
                        <label className="form-label" htmlFor="newPasswordConfirmation">
                          Yeni Şifre (Tekrar)
                        </label>
                        <input
                          className="form-control"
                          id="newPasswordConfirmation"
                          type="password"
                          autoComplete="new-password"
                          value={newPasswordConfirmation}
                          onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                          required
                        />
                      </div>
                      <Button className="btn btn-primary w-100 mb-2" type="submit" loading={loading}>
                        Şifremi Oluştur
                      </Button>
                      <button
                        type="button"
                        className="btn btn-link w-100"
                        onClick={handleRequestCode}
                        disabled={sendingCode}
                      >
                        Kodu tekrar gönder
                      </button>
                    </form>
                  )}

                  <div className="text-center mt-3">
                    <Link className="text-decoration-none" to={returnTo.startsWith('/') ? returnTo : '/profil'}>
                      <i className="fas fa-arrow-left me-1" />
                      Geri dön
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SifreOlustur;
